import { $getSelection, FORMAT_TEXT_COMMAND } from "lexical";
import {
  $patchStyleText,
  $getSelectionStyleValueForProperty,
} from "@lexical/selection";

// Lexical Formatting Utilities
// These functions can be used with any Lexical editor instance

/**
 * Apply bold formatting to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const applyBold = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Check if bold is already applied
      const isBold = selection.hasFormat("bold");
      if (!isBold) {
        // Apply bold format
        selection.formatText("bold");
      }
    }
  });
};

/**
 * Apply italic formatting to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const applyItalic = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Check if italic is already applied
      const isItalic = selection.hasFormat("italic");
      if (!isItalic) {
        // Apply italic format
        selection.formatText("italic");
      }
    }
  });
};

/**
 * Apply underline formatting to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const applyUnderline = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Check if underline is already applied
      const isUnderline = selection.hasFormat("underline");
      if (!isUnderline) {
        // Apply underline format
        selection.formatText("underline");
      }
    }
  });
};

/**
 * Apply highlight formatting to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const applyHighlight = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Check if highlight is already applied
      const isHighlighted = selection.hasFormat("highlight");
      if (!isHighlighted) {
        // Apply highlight format
        selection.formatText("highlight");
      }
    }
  });
};

/**
 * Apply text color to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @param {string} color - The color value (hex, rgb, etc.)
 */
export const applyTextColor = (editor, color) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      $patchStyleText(selection, { color });
    }
  });
};

/**
 * Apply font size to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @param {string} fontSize - The font size value (e.g., "16px", "1.2em")
 */
export const applyFontSize = (editor, fontSize) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      $patchStyleText(selection, { "font-size": fontSize });
    }
  });
};

/**
 * Apply background color to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @param {string} color - The background color value (hex, rgb, etc.)
 */
export const applyBackgroundColor = (editor, color) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      $patchStyleText(selection, { "background-color": color });
    }
  });
};

/**
 * Check if selected text is bold
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {boolean} - True if bold formatting is applied
 */
export const isBold = (editor) => {
  let result = false;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      result = selection.hasFormat("bold");
    }
  });
  return result;
};

/**
 * Check if selected text is italic
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {boolean} - True if italic formatting is applied
 */
export const isItalic = (editor) => {
  let result = false;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      result = selection.hasFormat("italic");
    }
  });
  return result;
};

/**
 * Check if selected text is underlined
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {boolean} - True if underline formatting is applied
 */
export const isUnderline = (editor) => {
  let result = false;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      result = selection.hasFormat("underline");
    }
  });
  return result;
};

/**
 * Check if selected text is highlighted
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {boolean} - True if highlight formatting is applied
 */
export const isHighlighted = (editor) => {
  let result = false;
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      const currentBgColor = $getSelectionStyleValueForProperty(
        selection,
        "background-color",
        "transparent"
      );

      // Normalize background color values for comparison
      const normalizedBgColor = currentBgColor?.toLowerCase();
      result =
        normalizedBgColor &&
        normalizedBgColor !== "transparent" &&
        normalizedBgColor !== "" &&
        normalizedBgColor !== "initial" &&
        normalizedBgColor !== "rgba(0, 0, 0, 0)" &&
        normalizedBgColor !== "rgb(255, 255, 255)";
    }
  });
  return result;
};

/**
 * Get the current text color of selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {string} - The current text color
 */
export const getTextColor = (editor) => {
  let result = "#000000";
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      const color = $getSelectionStyleValueForProperty(
        selection,
        "color",
        "#000000"
      );
      result = color;
    }
  });
  return result;
};

/**
 * Get the current background color of selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {string} - The current background color
 */
export const getBackgroundColor = (editor) => {
  let result = "transparent";
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      const bgColor = $getSelectionStyleValueForProperty(
        selection,
        "background-color",
        "transparent"
      );
      result = bgColor;
    }
  });
  return result;
};

/**
 * Toggle bold formatting for selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const toggleBold = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Toggle bold format
      selection.formatText("bold");
    }
  });
};

/**
 * Toggle italic formatting for selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const toggleItalic = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Toggle italic format
      selection.formatText("italic");
    }
  });
};

/**
 * Toggle underline formatting for selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const toggleUnderline = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Toggle underline format
      selection.formatText("underline");
    }
  });
};

/**
 * Toggle highlight formatting for selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const toggleHighlight = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Check if text is already highlighted by checking for background color
      const currentBgColor = $getSelectionStyleValueForProperty(
        selection,
        "background-color",
        "transparent"
      );

      // Normalize background color values for comparison
      const normalizedBgColor = currentBgColor?.toLowerCase();
      const isHighlighted =
        normalizedBgColor &&
        normalizedBgColor !== "transparent" &&
        normalizedBgColor !== "" &&
        normalizedBgColor !== "initial" &&
        normalizedBgColor !== "rgba(0, 0, 0, 0)" &&
        normalizedBgColor !== "rgb(255, 255, 255)";

      // If already highlighted (has a background color), remove it
      if (isHighlighted) {
        $patchStyleText(selection, { "background-color": "" });
      } else {
        // Apply yellow highlight background and ensure text visibility
        const currentTextColor = $getSelectionStyleValueForProperty(
          selection,
          "color",
          "#000000"
        );

        // DEBUG: Log the current text color before highlighting

        // Check if text color conflicts with yellow background
        const normalizedTextColor = currentTextColor?.toLowerCase();
        const isYellowText =
          normalizedTextColor === "#ffff00" ||
          normalizedTextColor === "rgb(255, 255, 0)" ||
          normalizedTextColor === "yellow" ||
          normalizedTextColor === "#ff0" ||
          normalizedTextColor === "rgba(255, 255, 0, 1)";

        // Check if text is very light (might be hard to see on yellow background)
        const isLightText =
          normalizedTextColor === "#ffffff" ||
          normalizedTextColor === "rgb(255, 255, 255)" ||
          normalizedTextColor === "white" ||
          normalizedTextColor === "#fff" ||
          normalizedTextColor === "rgba(255, 255, 255, 1)";

        // Always set background to yellow and ensure contrasting text color
        if (isYellowText || isLightText) {
          // Use black text for good contrast on yellow background

          $patchStyleText(selection, {
            "background-color": "#ffff00",
            color: "#000000",
          });
        } else {
          // Keep existing text color but add yellow background

          $patchStyleText(selection, { "background-color": "#ffff00" });
        }
      }
    }
  });
};

/**
 * Clear all formatting from selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const clearFormatting = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Remove all text formatting
      selection.removeText();
      // Re-insert the text without formatting
      const textContent = selection.getTextContent();
      selection.insertText(textContent);
    }
  });
};

/**
 * Get formatting state for the current selection
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @returns {Object} - Object containing formatting states
 */
export const getFormattingState = (editor) => {
  let state = {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isHighlighted: false,
    textColor: "#000000",
    backgroundColor: "transparent",
  };

  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      const backgroundColor = $getSelectionStyleValueForProperty(
        selection,
        "background-color",
        "transparent"
      );

      // Normalize background color values for comparison
      const normalizedBgColor = backgroundColor?.toLowerCase();
      const isHighlighted =
        normalizedBgColor &&
        normalizedBgColor !== "transparent" &&
        normalizedBgColor !== "" &&
        normalizedBgColor !== "initial" &&
        normalizedBgColor !== "rgba(0, 0, 0, 0)" &&
        normalizedBgColor !== "rgb(255, 255, 255)";

      const textColor = $getSelectionStyleValueForProperty(
        selection,
        "color",
        "#000000"
      );

      state = {
        isBold: selection.hasFormat("bold"),
        isItalic: selection.hasFormat("italic"),
        isUnderline: selection.hasFormat("underline"),
        isHighlighted: isHighlighted,
        textColor: textColor,
        backgroundColor: backgroundColor,
      };
    }
  });

  return state;
};

/**
 * Apply multiple formats to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 * @param {Array} formats - Array of format types to apply
 */
export const applyMultipleFormats = (editor, formats) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      formats.forEach((format) => {
        if (!selection.hasFormat(format)) {
          selection.formatText(format);
        }
      });
    }
  });
};

/**
 * Apply bold and italic together to selected text
 * @param {LexicalEditor} editor - The Lexical editor instance
 */
export const applyBoldAndItalic = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (selection && selection.isCollapsed() === false) {
      // Apply both bold and italic formats
      selection.formatText("bold");
      selection.formatText("italic");
    }
  });
};
